import axios, { type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { showToast, markInterceptorToast } from '@t-one-internal/design-system/lib/components/ui/sonner';
import { getLoginUrl } from '../utils/subdomain';
import { getAccessToken as getStoredAccessToken, setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken, clearTokens } from '../utils/tokenStorage';

// Environment-based API configuration
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/core/api/v1').replace(/\/+$/, '');
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:8000/core/public/v1';

// Global token storage
let authToken: string | null = null;
let refreshToken: string | null = null;

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Function to set the auth token
export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    setStoredAccessToken(token);
  } else {
    clearTokens();
  }
};

// Function to set the refresh token
export const setRefreshToken = (token: string | null) => {
  refreshToken = token;
  if (token) {
    setStoredRefreshToken(token);
  } else {
    // No-op: clearTokens() in setAuthToken(null) handles both
  }
};

// Function to get the auth token
export const getAuthToken = (): string | null => {
  if (!authToken) {
    // Try to get from storage as fallback (cookies + localStorage)
    // Check admin-portal tokens first (prefixed with admin_), then regular tokens
    authToken = localStorage.getItem('admin_access_token') ||
      getStoredAccessToken();
  }
  return authToken;
};

// Subscribe to token refresh
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Refresh the access token using the refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    console.log('🔄 Attempting to refresh access token...');

    if (!refreshToken) {
      // Try to get refresh token from localStorage
      refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        console.error('❌ No refresh token available');
        return null;
      }
    }

    const response = await axios.post(
      `${PUBLIC_API_BASE_URL}/account/refresh-token`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }
    );

    if (response.data && response.data.access_token) {
      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;

      // Update tokens in memory
      authToken = newAccessToken;
      if (newRefreshToken) {
        refreshToken = newRefreshToken;
      }

      // Update tokens in storage (cookies + localStorage)
      setStoredAccessToken(newAccessToken);
      if (newRefreshToken) {
        setStoredRefreshToken(newRefreshToken);
      }

      console.log('✅ Token refreshed successfully');
      return newAccessToken;
    }

    console.error('❌ Invalid refresh token response');
    return null;
  } catch (error) {
    console.error('❌ Token refresh failed:', error);

    // Clear tokens on refresh failure
    authToken = null;
    refreshToken = null;
    clearTokens();

    return null;
  }
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});
authToken = getStoredAccessToken();
// Request interceptor to add JWT token and impersonation token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();

    if (token) {
      console.log('🔐 JWT Token found in localStorage:', authToken);
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 JWT Token attached to request:', config.url);
    } else {
      console.warn('⚠️ No JWT token available for request:', config.url);
    }

    // Add impersonation token if active (stored in localStorage)
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    console.log('🔍 Checking for impersonation_data:', impersonationDataStr ? 'FOUND' : 'NOT FOUND');
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        console.log('🔍 Parsed impersonation data:', {
          hasSessionToken: !!impersonationData.sessionToken,
          sessionTokenPreview: impersonationData.sessionToken ? impersonationData.sessionToken.substring(0, 20) + '...' : 'MISSING',
        });
        if (impersonationData.sessionToken) {
          console.log('👤 Adding impersonation token to request:', config.url);
          config.headers['X-Impersonation-Token'] = impersonationData.sessionToken;
        }
      } catch (e) {
        console.error('❌ Failed to parse impersonation_data:', e);
      }
    } else {
      console.log('⚠️ No impersonation_data in localStorage for request:', config.url);
    }

    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling, token refresh, and logging
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  async (error: AxiosError) => {
    const { response, config } = error;
    const originalRequest = config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if we're in an impersonation session
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    const isImpersonating = !!impersonationDataStr;

    // Handle different error scenarios
    if (response) {
      const { status, data } = response;

      console.error(`❌ API Error: ${status} ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`);
      console.error('📦 Error response:', data);

      // Handle 403 Forbidden - Tenant resource mismatch (cross-tenant access)
      // Uses a fixed toast id so concurrent 403s only show one toast.
      // Marks error as _toasted so component catch blocks can skip their own toast.
      if (status === 403 && (data as any)?.code === 'TENANT_RESOURCE_MISMATCH') {
        console.error('🚫 Tenant resource mismatch - cross-tenant access denied');
        markInterceptorToast();
        showToast.error('Access Denied', {
          id: 'tenant-resource-mismatch',
          description: 'You don\'t have permission to access this resource. It may belong to a different organization.',
        });
        (error as any)._toasted = true;
        return Promise.reject(error);
      }

      // Handle 403 Forbidden - Account Locked (skip during impersonation)
      if (status === 403 && (data as any)?.code === 'ACCOUNT_LOCKED' && !isImpersonating) {
        console.error('🔒 Account has been locked');

        // Clear all tokens
        authToken = null;
        refreshToken = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.clear();

        // Redirect to login with error message
        window.location.href = getLoginUrl('error=account_locked');

        return Promise.reject(error);
      }

      // Handle 401 Unauthorized - Token expired or invalid
      if (status === 401 && originalRequest && !originalRequest._retry) {
        console.log('🔄 401 Unauthorized - Attempting token refresh...');

        // During impersonation, don't try to refresh token or redirect to login
        if (isImpersonating) {
          console.log('👤 401 during impersonation - not triggering logout');
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // If already refreshing, queue this request
          console.log('⏳ Token refresh in progress, queuing request...');

          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            });
          });
        }

        // Mark this request as retried to avoid infinite loops
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            // Token refreshed successfully
            isRefreshing = false;

            // Notify all queued requests
            onTokenRefreshed(newToken);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            console.log('🔄 Retrying original request with new token...');
            return apiClient(originalRequest);
          } else {
            // Token refresh failed
            isRefreshing = false;
            console.error('🚫 Token refresh failed - Clearing auth state');

            // Clear tokens
            authToken = null;
            refreshToken = null;
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');

            return Promise.reject(error);
          }
        } catch (refreshError) {
          isRefreshing = false;
          console.error('❌ Error during token refresh:', refreshError);

          // Clear tokens on refresh error
          authToken = null;
          refreshToken = null;
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');

          return Promise.reject(error);
        }
      }
    } else if (error.request) {
      console.error('❌ Network Error: No response received');
    } else {
      console.error('❌ Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Listen for token refresh events from the inactivity service
if (typeof window !== 'undefined') {
  window.addEventListener('token-refreshed', ((event: CustomEvent) => {
    const { accessToken } = event.detail;
    if (accessToken) {
      console.log('🔄 [API Client] Token refreshed by inactivity service - updating in-memory token');
      authToken = accessToken;
      // No need to update localStorage here - the inactivity service already did that
    }
  }) as EventListener);
}

export default apiClient;
export { API_BASE_URL, PUBLIC_API_BASE_URL };
